/**
 * `PerformanceCapture`, runtime ingest of timed events.
 *
 * Pure TS, zero dependency on React / Studio / Pixi / DOM. The bridge
 * (chantier 7) feeds this class with three streams:
 *
 *   - `recordPress` for each note-on  (timestamped at `performance.now()`)
 *   - `recordRelease` for each note-off
 *   - `recordBeat` for each metronome beat (carries `realTimeMs`)
 *
 * The detectors (chantier 3) consume the buffered data through the
 * `PerformanceCaptureQuery` interface, which keeps the runtime and
 * the detectors loosely coupled and trivially testable in isolation.
 *
 * Buffering policy
 * ----------------
 * The buffer is bounded by the run first: `clear()` is called by the run
 * orchestrator when a pass is armed. Inside a single run we keep all
 * events, phrases are short and the volume stays manageable.
 *
 * It is also bounded by size, because the bridges record *always* — every
 * press and every click of the studio, through S0 → S3 and free play alike —
 * and nothing empties the buffer until the next pass is armed. Past
 * `MAX_EVENTS` the oldest half is dropped: a pass reads far fewer events
 * than that, so what a run needs is never evicted.
 *
 * Order
 * -----
 * `recordPress` / `recordRelease` / `recordBeat` are expected to be
 * called in monotonically non-decreasing time order. Out-of-order
 * inserts are tolerated (no exception is thrown) but the queries
 * that rely on order (`getBeatAt`, `getMedianInterGroupIntervalMs`)
 * sort defensively before computing.
 */

import { SIMULTANEOUS_GROUPING_WINDOW_MS } from '../thresholds';
import type {
  PerformanceCaptureQuery,
  RecordedBeat,
  RecordedPress,
} from '../types';

/**
 * One release event. Kept opaque from the public query surface for
 * now, only the lifecycle methods expose them. We track them so a
 * future detector (e.g. sustain-pedal heuristics, or pressure-time
 * metrics) can consume them without an API rewrite.
 */
type RecordedRelease = {
  readonly midi: number;
  readonly tMs: number;
};

/** Plafond de chaque tampon ; au-delà, la moitié la plus ancienne part. */
export const MAX_EVENTS = 4096;

export class PerformanceCapture implements PerformanceCaptureQuery {
  private presses: RecordedPress[] = [];
  private releases: RecordedRelease[] = [];
  private beats: RecordedBeat[] = [];

  /** Wall-clock instant the current run started (set by `markRunStart`). */
  private runStartMs: number | null = null;

  // ── Ingest ─────────────────────────────────────────────────────

  recordPress(midi: number, tMs: number, velocity?: number): void {
    bounded(this.presses).push({ midi, tMs, velocity });
  }

  recordRelease(midi: number, tMs: number): void {
    bounded(this.releases).push({ midi, tMs });
  }

  recordBeat(beat: RecordedBeat): void {
    bounded(this.beats).push(beat);
  }

  /**
   * Mark the logical start of a run. Useful for windows expressed
   * relative to the run origin (e.g. "press in the first 8 beats
   * after the count-in"). Does NOT clear the buffer, call `clear()`
   * separately if needed.
   */
  markRunStart(tMs: number): void {
    this.runStartMs = tMs;
  }

  /** Logical run-start timestamp, or `null` if `markRunStart` was never called. */
  getRunStartMs(): number | null {
    return this.runStartMs;
  }

  /** Reset every buffer and the run-start marker. */
  clear(): void {
    this.presses = [];
    this.releases = [];
    this.beats = [];
    this.runStartMs = null;
  }

  // ── Queries ────────────────────────────────────────────────────

  getPresses(): readonly RecordedPress[] {
    return this.presses;
  }

  getPressesInWindow(t0Ms: number, t1Ms: number): RecordedPress[] {
    const lo = Math.min(t0Ms, t1Ms);
    const hi = Math.max(t0Ms, t1Ms);

    return this.presses.filter((p) => p.tMs >= lo && p.tMs <= hi);
  }

  getBeats(): readonly RecordedBeat[] {
    return this.beats;
  }

  getBeatAt(tMs: number): RecordedBeat | null {
    if (this.beats.length === 0) {
      return null;
    }

    // Defensive sort by realTimeMs (ingest order is expected to be
    // monotonic, but we don't trust the bridge to enforce it).
    const sorted = [...this.beats].sort(
      (a, b) => a.realTimeMs - b.realTimeMs,
    );

    let result: RecordedBeat | null = null;

    for (const beat of sorted) {
      if (beat.realTimeMs <= tMs) {
        result = beat;
        continue;
      }

      break;
    }

    return result;
  }

  getMedianInterGroupIntervalMs(): number {
    const groupTimes = this.computeGroupTimes();

    if (groupTimes.length < 2) {
      return 0;
    }

    const intervals: number[] = [];
    for (let i = 1; i < groupTimes.length; i += 1) {
      intervals.push(groupTimes[i] - groupTimes[i - 1]);
    }

    return median(intervals);
  }

  // ── Internal ───────────────────────────────────────────────────

  /**
   * Group the buffered presses into simultaneous clusters and return
   * the representative timestamp of each group (the time of its
   * first press).
   *
   * A new group starts whenever the gap to the previous press
   * exceeds `SIMULTANEOUS_GROUPING_WINDOW_MS`. This means three
   * presses at t=10, 25, 40 form a single group (each gap ≤ 30 ms),
   * even though the first-to-last span is 30 ms, the window is on
   * adjacent presses, which matches a chord rolled across a few ms.
   */
  private computeGroupTimes(): number[] {
    if (this.presses.length === 0) {
      return [];
    }

    const sorted = [...this.presses].sort((a, b) => a.tMs - b.tMs);

    const groupTimes: number[] = [sorted[0].tMs];
    let lastTime = sorted[0].tMs;

    for (let i = 1; i < sorted.length; i += 1) {
      const t = sorted[i].tMs;

      if (t - lastTime > SIMULTANEOUS_GROUPING_WINDOW_MS) {
        groupTimes.push(t);
      }

      lastTime = t;
    }

    return groupTimes;
  }
}

// ── Helpers ──────────────────────────────────────────────────────

/**
 * Fait de la place dans `buffer` avant un ajout, en évacuant la moitié la
 * plus ancienne d'un coup : un `shift()` à chaque événement serait linéaire
 * à chaque fois, ceci l'est une fois toutes les `MAX_EVENTS / 2` entrées.
 */
function bounded<T>(buffer: T[]): T[] {
  if (buffer.length >= MAX_EVENTS) {
    buffer.splice(0, MAX_EVENTS >> 1);
  }

  return buffer;
}

function median(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }

  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 1) {
    return sorted[mid];
  }

  return (sorted[mid - 1] + sorted[mid]) / 2;
}
